import { useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Plus, Sprout } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { RecordDialog } from "@/components/health/Records";

const W = 320, H = 180, P = 28;
const num = (s) => parseFloat(String(s || "").replace(",", "."));

function Chart({ points, unit, rgb, testid }) {
  const reduce = useReducedMotion();
  const geo = useMemo(() => {
    if (points.length < 1) return null;
    const ts = points.map((p) => new Date(p.date).getTime()), vs = points.map((p) => p.v);
    const t0 = Math.min(...ts), t1 = Math.max(...ts) || t0 + 1, v0 = Math.min(...vs) * 0.95, v1 = Math.max(...vs) * 1.05 || v0 + 1;
    const xy = points.map((p, i) => [P + ((ts[i] - t0) / (t1 - t0 || 1)) * (W - 2 * P), H - P - ((p.v - v0) / (v1 - v0 || 1)) * (H - 2 * P)]);
    return { xy, d: xy.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ") };
  }, [points]);
  if (!geo) return null;
  const last = geo.xy[geo.xy.length - 1];
  const dur = reduce ? 0 : 1.6;
  return (
    <figure className="q-card min-w-0 p-3" data-testid={testid} data-reduced-motion={reduce ? "true" : "false"}>
      <figcaption className="mb-1 text-sm font-semibold text-q-text">{unit}</figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full max-w-full" role="img" aria-label={`${unit} over time`}>
        <line x1={P} y1={H - P} x2={W - P} y2={H - P} stroke="currentColor" className="text-q-line" />
        <motion.path d={geo.d} fill="none" stroke={`rgb(${rgb})`} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
          initial={reduce ? false : { pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: dur, ease: "easeInOut" }} />
        {geo.xy.map(([x, y], i) => (
          <motion.circle key={i} cx={x} cy={y} r="5" fill={`rgb(${rgb})`} initial={reduce ? false : { scale: 0 }} animate={{ scale: 1 }} transition={{ delay: reduce ? 0 : (dur * i) / Math.max(1, geo.xy.length - 1), duration: reduce ? 0 : 0.3 }} />
        ))}
        {points.map((p, i) => <text key={`t${i}`} x={geo.xy[i][0]} y={geo.xy[i][1] + 20} textAnchor="middle" fontSize="10" fill="currentColor" className="text-q-muted">{p.label}</text>)}
        <motion.g initial={reduce ? false : { x: P - last[0], y: H - P - last[1], opacity: 0 }} animate={{ x: 0, y: 0, opacity: 1 }} transition={{ duration: dur, ease: "easeInOut" }} data-testid={`${testid}-character`}>
          <motion.g animate={reduce ? {} : { rotate: [0, -8, 8, 0] }} transition={{ repeat: reduce ? 0 : Infinity, duration: 2.4, delay: dur }} style={{ originX: `${last[0]}px`, originY: `${last[1]}px` }}>
            <foreignObject x={last[0] - 12} y={last[1] - 40} width="24" height="24"><Sprout className="h-6 w-6" style={{ color: `rgb(var(--glow-money))` }} /></foreignObject>
          </motion.g>
        </motion.g>
      </svg>
    </figure>
  );
}

export default function Growth({ base, items, canEdit, locked, reload }) {
  const [add, setAdd] = useState(false);
  const series = (k) => items.filter((g) => !Number.isNaN(num(g[k]))).map((g) => ({ date: g.date, v: num(g[k]), label: g[k] }));
  return (
    <section data-testid="health-growth">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-lg font-extrabold text-q-text">Growth</h3>
        {canEdit && <button className="q-btn q-btn-secondary min-h-[44px]" disabled={locked} onClick={() => setAdd(true)} data-testid="health-add-growth"><Plus className="h-4 w-4" /> Add measurement</button>}
      </div>
      <div className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2">
        {series("height").length > 0 && <Chart points={series("height")} unit="Height" rgb="var(--glow-money)" testid="growth-chart-height" />}
        {series("weight").length > 0 && <Chart points={series("weight")} unit="Weight" rgb="var(--glow-kids)" testid="growth-chart-weight" />}
        {series("head").length > 0 && <Chart points={series("head")} unit="Head size" rgb="var(--glow-calendar)" testid="growth-chart-head" />}
      </div>
      <ul className="mt-2 space-y-1 text-sm text-q-muted">{items.map((g) => <li key={g.id} data-testid={`growth-${g.id}`}>{fmtDate(g.date)} · {[g.height, g.weight, g.head].filter(Boolean).join(" · ")}</li>)}</ul>
      {add && <RecordDialog base={base} group="growth" onClose={() => setAdd(false)} onSaved={() => { setAdd(false); reload(); }} />}
    </section>
  );
}
